import { useState, useEffect } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Typography, Checkbox, TextField, CircularProgress,
} from "@mui/material";
import Papa from "papaparse";
import useStyles from "./ExportModal.styles";
import { CircleNotch, X, Check, WarningCircle, Info } from "phosphor-react";
import CustomPagination from "../CustomPagination/CustomPagination.component";

const EXPORT_PAGE_SIZE = 50;

const CheckboxIcon = () => (
    <span style={{
        width: 20,
        height: 20,
        borderRadius: 6,
        border: "2px solid #3D4357",
        backgroundColor: "#252A38",
        display: "inline-block",
        boxSizing: "border-box",
    }} />
);

const CheckboxCheckedIcon = () => (
    <span style={{
        width: 20,
        height: 20,
        borderRadius: 6,
        backgroundColor: "#6B6FDE",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
    }}>
        <Check size={13} color="#fff" weight="bold" />
    </span>
);

const ExportModal = ({ headersList, valuesList, fetchAllData, handleClose }) => {
    const classes = useStyles();

    const [allDataList, setAllDataList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);

    const [headerCustomNames, setHeaderCustomNames] = useState({});
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [currentExportPage, setCurrentExportPage] = useState(1);

    // Fetch all data once on mount
    useEffect(() => {
        const loadAllData = async () => {
            setIsLoading(true);
            setFetchError(null);
            try {
                const data = await fetchAllData();
                setAllDataList(data);
                setSelectedRows(data.map((item) => ({ id: item.id, selected: true })));
            } catch (err) {
                console.error("ExportModal fetch error:", err);
                setFetchError("Failed to fetch data. Please close and try again.");
            } finally {
                setIsLoading(false);
            }
        };
        loadAllData();
    }, []);

    // Initialise column selections and custom header names whenever headersList changes
    useEffect(() => {
        setSelectedColumns(headersList.map((header) => ({ name: header, selected: true })));
        setHeaderCustomNames(
            headersList.reduce((acc, header) => ({ ...acc, [header]: header }), {})
        );
    }, [headersList]);

    const handleColumnSelection = (headerName) => {
        setSelectedColumns((prev) =>
            prev.map((col) => ({ ...col, selected: col.name === headerName ? !col.selected : col.selected }))
        );
    };

    const handleRowSelection = (id) => {
        setSelectedRows((prev) =>
            prev.map((row) => ({ ...row, selected: row.id === id ? !row.selected : row.selected }))
        );
    };

    const handleSelectAllRows = (checked) => {
        setSelectedRows((prev) => prev.map((row) => ({ ...row, selected: checked })));
    };

    const handleSelectAllColumns = (checked) => {
        setSelectedColumns((prev) => prev.map((col) => ({ ...col, selected: checked })));
    };

    const getDisplayValue = (item, value) => {
        const raw = item[value.name];
        if (value.type === "string" || value.type === "number") {
            return raw;
        } else if (value.type === "date") {
            if (!raw) return "";
            return new Intl.DateTimeFormat("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            }).format(new Date(raw));
        } else if (value.type === "array") {
            if (!Array.isArray(raw)) return raw ?? "";
            if (value.key) return raw.map((item) => item?.[value.key] ?? "").join(", ");
            return raw.join(", ");
        } else if (value.type === "object") {
            if (value.key2) return raw?.[value.key] + " " + raw?.[value.key2] ?? "";
            return raw?.[value.key] ?? "";
        }
        return raw ?? "";
    }

    const exportToCSV = () => {
        const activeColIndices = selectedColumns
            .map((col, i) => (col.selected ? i : null))
            .filter((i) => i !== null);

        const filteredRows = allDataList.filter(
            (item) => selectedRows.find((row) => row.id === item.id)?.selected
        );

        const exportData = filteredRows.map((item) => {
            const row = {};
            activeColIndices.forEach((colIdx) => {
                const customName = headerCustomNames[headersList[colIdx]] || headersList[colIdx];
                row[customName] = getDisplayValue(item, valuesList[colIdx]);
            });
            return row;
        });

        const csv = Papa.unparse(exportData);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "export.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const eachColumnWidth = Math.floor(95 / headersList.length);
    const allColumnsSelected = selectedColumns.length > 0 && selectedColumns.every((c) => c.selected);
    const allRowsSelected = selectedRows.length > 0 && selectedRows.every((r) => r.selected);
    const selectedRowCount = selectedRows.filter((r) => r.selected).length;
    const totalExportPages = Math.max(1, Math.ceil(allDataList.length / EXPORT_PAGE_SIZE));
    const pagedDataList = allDataList.slice(
        (currentExportPage - 1) * EXPORT_PAGE_SIZE,
        currentExportPage * EXPORT_PAGE_SIZE
    );

    return (
        <div style={{
            width: "95%",
            height: "95%",
            backgroundColor: "#1B1D28",
            borderRadius: "20px",
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}>
            {/* Header bar */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexShrink: 0 }}>
                <div>
                    <Typography variant="h6">Export to CSV</Typography>
                    {!isLoading && !fetchError && (
                        <Typography variant="body2" style={{ color: "#8A8FA8", marginTop: 4, fontSize: 12 }}>
                            {allDataList.length} total records &nbsp;·&nbsp; {selectedRowCount} row{selectedRowCount !== 1 ? "s" : ""} selected
                        </Typography>
                    )}
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                    <Button
                        className={classes.containedButton}
                        variant="contained"
                        onClick={exportToCSV}
                        disabled={isLoading || !!fetchError || selectedRowCount === 0}
                        startIcon={<CircleNotch size={18} />}
                    >
                        Export to CSV
                    </Button>
                    <Button
                        variant="outlined"
                        className={classes.outlineButton}
                        onClick={handleClose}
                        startIcon={<X size={18} />}
                    >
                        Cancel
                    </Button>
                </div>
            </div>

            {/* Body */}
            {isLoading ? (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
                    <CircularProgress style={{ color: "#6B6FDE" }} size={40} />
                    <Typography style={{ color: "#8A8FA8" }}>Fetching all data, please wait…</Typography>
                </div>
            ) : fetchError ? (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
                    <WarningCircle size={40} color="#E05252" />
                    <Typography style={{ color: "#E05252" }}>{fetchError}</Typography>
                </div>
            ) : (
                <>
                    <TableContainer className={classes.tableContainer}>
                        <Table>
                            <TableHead style={{ display: "table", width: "100%", tableLayout: "fixed" }}>
                                <TableRow>
                                    <TableCell className={classes.headerCell} style={{ width: "5%" }}>
                                        <Checkbox
                                            size="small"
                                            icon={<CheckboxIcon />}
                                            checkedIcon={<CheckboxCheckedIcon />}
                                            checked={allRowsSelected}
                                            onChange={(e) => handleSelectAllRows(e.target.checked)}
                                        />
                                    </TableCell>
                                    {headersList.map((header, idx) => (
                                        <TableCell key={header} className={classes.headerCell} style={{ width: `${eachColumnWidth}%` }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
                                                <Checkbox
                                                    size="small"
                                                    icon={<CheckboxIcon />}
                                                    checkedIcon={<CheckboxCheckedIcon />}
                                                    checked={selectedColumns[idx]?.selected ?? true}
                                                    onChange={() => handleColumnSelection(header)}
                                                />
                                                <span>{header}</span>
                                                <span className={classes.lightText}>as</span>
                                            </div>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={headerCustomNames[header] ?? header}
                                                onChange={(e) =>
                                                    setHeaderCustomNames({ ...headerCustomNames, [header]: e.target.value })
                                                }
                                                className={classes.textField}
                                                sx={{ bgcolor: "#252A38", color: "#f9f9f9" }}
                                                InputProps={{ sx: { bgcolor: "#252A38", color: "#f9f9f9" } }}
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody style={{ display: "block", overflowY: "auto", height: "calc(95vh - 340px)" }}>
                                {pagedDataList.map((item) => (
                                    <TableRow key={item.id} style={{ display: "table", width: "100%", tableLayout: "fixed" }}>
                                        <TableCell className={classes.tableCell} style={{ width: "5%" }}>
                                            <Checkbox
                                                size="small"
                                                icon={<CheckboxIcon />}
                                                checkedIcon={<CheckboxCheckedIcon />}
                                                checked={selectedRows.find((r) => r.id === item.id)?.selected ?? true}
                                                onChange={() => handleRowSelection(item.id)}
                                            />
                                        </TableCell>
                                        {valuesList.map((value) => (
                                            <TableCell key={value.name} className={classes.tableCell} style={{ width: `${eachColumnWidth}%` }}>
                                                {getDisplayValue(item, value)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {totalExportPages > 1 && <Typography style={{ display: "flex", alignItems: "center", gap: 4, color: "#B1B5C9" }}><Info size={16} /> The table is paginated here but will be exported as a single page.</Typography>}
                    <div style={{ position: "relative", height: "50px", flexShrink: 0 }}>
                        <CustomPagination
                            currentPage={currentExportPage}
                            totalPage={totalExportPages}
                            totalItem={allDataList.length}
                            handlePageChange={(page) => setCurrentExportPage(page)}
                            setIsNewPageLoading={() => { }}
                            trackable="Table Items"
                        />
                    </div>
                </>
            )
            }
        </div >
    );
};

export default ExportModal;
